import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

type AddressValue = StripeAddressElementChangeEvent['value'];

export interface IMapBillingInput {
  email: string;
  company?: string;
  address: {
    complete: boolean;
    value: AddressValue | null;
  };
}

/** billing*-prefixed payload shape (Step7 Post/Reactivate + Upgrade Step2). */
export interface IBillingPrefixed {
  billingFirstName: string;
  billingLastName: string;
  billingEmail: string;
  billingPhone: string;
  billingCompany: string;
  billingCountry: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingZip: string;
  billingState: string;
  billingProvince: string;
}

/** Unprefixed payload shape (Prepaid Steps3). */
export interface IBillingUnprefixed {
  firstName: string;
  lastName: string;
  country: string;
  address: string;
  city: string;
  state: string;
  province: string;
  zip: string;
}

export type MapBillingResult =
  | { ok: false }
  | { ok: true; prefixed: IBillingPrefixed; unprefixed: IBillingUnprefixed };

/**
 * Split a Stripe full-name string into first / last. First whitespace token is
 * the first name, the remainder is the last name; a single token is duplicated
 * into both (the legacy split-card flow required both billing name fields).
 */
export const splitName = (name: string): { first: string; last: string } => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: parts[0] };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

/**
 * Maps the onChange-captured Link email + Billing Company + AddressElement value
 * to BOTH legacy finalize-payload shapes. Returns `{ ok: false }` when the
 * address is incomplete so the caller can block submit with an inline error.
 *
 * US addresses populate `billingState`/`state`; everything else populates
 * `billingProvince`/`province` (mirrors the old useIsProvince behaviour so the
 * server's billing validation keeps receiving values in the expected field).
 */
export const mapBillingToPayload = ({ email, company, address }: IMapBillingInput): MapBillingResult => {
  if (!address.complete || !address.value) {
    return { ok: false };
  }

  const { name, phone, address: addr } = address.value;
  const { first, last } = splitName(name);
  const isUs = addr.country === 'US';
  const region = addr.state || '';

  const prefixed: IBillingPrefixed = {
    billingFirstName: first,
    billingLastName: last,
    billingEmail: email || '',
    billingPhone: phone || '',
    billingCompany: company || '',
    billingCountry: addr.country || '',
    billingAddress1: addr.line1 || '',
    billingAddress2: addr.line2 || '',
    billingCity: addr.city || '',
    billingZip: addr.postal_code || '',
    billingState: isUs ? region : '',
    billingProvince: isUs ? '' : region,
  };

  const unprefixed: IBillingUnprefixed = {
    firstName: first,
    lastName: last,
    country: addr.country || '',
    address: addr.line1 || '',
    city: addr.city || '',
    state: isUs ? region : '',
    province: isUs ? '' : region,
    zip: addr.postal_code || '',
  };

  return { ok: true, prefixed, unprefixed };
};

export default mapBillingToPayload;
